import React, { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { toast } from '../ui/sonner';
import { apiCall } from '../../utils/api';

/** P2P wallet transfer — send funds to another user by email. */
export default function SendMoneyModal({ balance, onClose, onSuccess }) {
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    const amt = parseFloat(amount);
    if (!email.trim()) { toast.error('Enter the recipient email'); return; }
    if (Number.isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (amt > balance) { toast.error('Insufficient wallet balance'); return; }
    setSending(true);
    try {
      const res = await apiCall('/api/wallet/send', {
        method: 'POST',
        body: JSON.stringify({ recipient_email: email.trim(), amount: amt, note }),
      });
      toast.success(res.message || `$${amt.toFixed(2)} sent`);
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.message || 'Transfer failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="send-money-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Send Money</h3>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="send-money-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Recipient email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="send-money-email"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">
              Amount <span className="text-gray-400">(available ${Number(balance || 0).toFixed(2)})</span>
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="send-money-amount"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Note (optional)</label>
            <input
              type="text"
              maxLength={200}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What's it for?"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="send-money-note"
            />
          </div>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-50"
            data-testid="send-money-submit"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Money
          </button>
        </div>
      </div>
    </div>
  );
}
